// gestureLookup — the ONE rule that turns a server gesture name into a mapping.
//
// A gesture name arrives from convorcher PascalCased and space-stripped
// (`ToPascalCase` in its animation extractor: "eye roll" -> "EyeRoll"), while
// the runtime map is keyed by the gesture row's own `tag`, lowercased
// (gestureFields.js). Resolving between the two used to live inline in
// FaceClipPlayer.playGesture, which meant anything ELSE that wanted to ask a
// question about the incoming gesture — "does this one have a face clip?" —
// had to reimplement the same three steps and could silently disagree with
// what playGesture actually plays. One function, both callers.
//
// The three steps, in order, unchanged from the inline version:
//   1. exact match on the lowercased name
//   2. substring: the first key the name CONTAINS, so a verbose server tag
//      ("leans in, grin spreads") still finds the short key ("lean in")
//   3. the '*' wildcard row, when the character defines one
//
// An EMPTY name resolves to null rather than falling through to '*' — an
// absent gesture is not an unknown gesture, and playGesture's early return has
// always treated it that way. Every sentence without an action carries '', so
// without this rule each of them would play the wildcard.

/**
 * The mapping a gesture name resolves to, or null.
 *
 * @param {Record<string, {face?: string|null, body?: string|null, duration?: number, bodyWeight?: number}>} gestures
 *   the runtime gesture map (FaceClipPlayer._gestures)
 * @param {string} name  the incoming gesture name, any casing/spacing
 * @returns {object|null}
 */
export function resolveGestureEntry(gestures, name) {
  if (!gestures || !name) return null
  const needle = String(name).toLowerCase().trim()
  if (!needle) return null

  const exact = gestures[needle]
  if (exact) return exact

  const key = Object.keys(gestures).find(k => k !== '*' && needle.includes(k))
  if (key) return gestures[key]

  return gestures['*'] ?? null
}

/**
 * Whether the gesture this name resolves to carries a FACE clip.
 *
 * The question the event graph asks to decide how to treat a sentence's
 * gesture: a face gesture wants a lead before speech (LiveLink takes the face
 * outright the moment audio starts — see faceFrame.js), a body-only one can
 * play additively with the sentence and needs no special handling.
 *
 * False for an unresolved name AND for a resolved row with no face animation —
 * both mean "nothing on the face", which is what the caller is deciding about.
 * Use resolveGestureEntry directly to tell the two apart.
 */
export function gestureHasFace(gestures, name) {
  return !!resolveGestureEntry(gestures, name)?.face
}
